import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaTrophy } from "react-icons/fa";
import { MdOutlineFaceRetouchingOff, MdOutlineGroups2 } from "react-icons/md";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { ReactComponent as BluePenIcon } from "../../../assets/blue_pen_icon.svg";
import { ReactComponent as CertificateIcon } from "../../../assets/cerificate_icon.svg";
import { ReactComponent as GreenCheckIcon } from "../../../assets/green_check_icon.svg";
import { ReactComponent as ClassroomIcon } from "../../../assets/Group322.svg";
import { ReactComponent as OpenBookIcon } from "../../../assets/open_book_icon.svg";
import { ReactComponent as PulpPlaceholder } from "../../../assets/pulp_icon.svg";
import { ReactComponent as RatedIcon } from "../../../assets/rated_icon.svg";
import { ReactComponent as RedCloseIcon } from "../../../assets/red_close_icon.svg";
import stars_icon from "../../../assets/stars_transparent.png";
import user_placeholder from "../../../assets/user-profile.jpg";
import Button from "../../../components/Button";
import LoadingSpinner from "../../../components/LoadingSpinner";
import AssignStudentModal from "../../../components/modals/common/AssignStudentModal";
import DeleteStudentModal from "../../../components/modals/common/DeleteStudentModal";
import EditStudentModal from "../../../components/modals/common/EditStudentModal";
import UnassignStudentModal from "../../../components/modals/common/UnassignStudentModal";
import { USER_TYPE } from "../../../constants/common";
import {
  resetBreadcrumbs,
  setBreadcrumbDynamicID,
  setBreadcrumbDynamicPathNames,
} from "../../../redux/slices/breadcrumbsSlice";
import studentServices from "../../../services/studentServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const SchoolStudentDetails = () => {
  const { id: studentID } = useParams();
  const { t } = useTranslation();
  const lang = getLanguage();
  const dispatch = useDispatch();
  const navigate = useNavigateWithLang();

  const [loading, setLoading] = useState(true);
  const [studentDetails, setStudentDetails] = useState(null);
  const [studentCourses, setStudentCourses] = useState(null);
  const [studentLessons, setStudentLessons] = useState(null);
  const [studentScoreStats, setStudentScoreStats] = useState(null);
  const [studentProjectsStats, setStudentProjectsStats] = useState(null);

  const assignStudentModalRef = useRef();
  const unassignStudentModalRef = useRef();
  const deleteStudentModalRef = useRef();
  const editStudentModalRef = useRef();

  const openAssignStudentModal = (student) => assignStudentModalRef.current.openModal(student);
  const openUnassignStudentModal = (student) => unassignStudentModalRef.current.openModal(student);
  const openDeleteStudentModal = (student) => deleteStudentModalRef.current.openModal(student);
  const openEditStudentModal = (student) => editStudentModalRef.current.openModal(student);

  const handleAssignStudentSuccess = () => {
    getStudentDetails();
  };

  const handleUnassignStudentSuccess = () => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/students`);
  };

  const handleDeleteStudentSuccess = () => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/students`);
  };

  const handleEditStudentSuccess = () => {
    getStudentDetails();
  };

  const navigateClassroomDetails = (id) => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/classrooms/${id}`);
  };

  const getStudentDetails = async () => {
    try {
      const response = await studentServices.getStudentByID(studentID);
      setStudentDetails(response);
      dispatch(
        setBreadcrumbDynamicPathNames([
          response?.classroom?.teacher?.name,
          response?.classroom?.name,
          response?.name,
        ])
      );
      dispatch(setBreadcrumbDynamicID([response?.classroom?.teacher?.id, response?.classroom?.id]));
    } catch (error) {
      console.error("Error getting student details", error);
    } finally {
      setLoading(false);
    }
  };

  const getStudentCourses = async () => {
    try {
      const response = await studentServices.getStudentCourses(studentID);
      setStudentCourses(response);
    } catch (error) {
      console.error("Error getting student courses", error);
    }
  };
  const getStudentLessons = async () => {
    try {
      const response = await studentServices.getStudentLessons(studentID);
      setStudentLessons(response);
    } catch (error) {
      console.error("Error getting student lessons", error);
    }
  };

  const getStudentProjectsStats = async () => {
    try {
      const response = await studentServices.getStudentProjectsStats(studentID);
      setStudentProjectsStats(response);
    } catch (error) {
      console.error("Error getting student projects stats", error);
    }
  };

  const getStudentScoreStats = async () => {
    try {
      const response = await studentServices.getStudentScoreStats(studentID);
      setStudentScoreStats(response);
      console.log("response", response);
    } catch (error) {
      console.error("Error getting student score stats", error);
    }
  };

  useEffect(() => {
    getStudentDetails();
    getStudentCourses();
    getStudentLessons();
    getStudentScoreStats();
    getStudentProjectsStats();
  }, [lang]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      dispatch(resetBreadcrumbs());
    };
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-start lg:space-x-2">
        <Button
          iconLeft={<MdOutlineGroups2 size={22} />}
          color="coral"
          label={t("assign_to_classroom")}
          onClick={() => openAssignStudentModal(studentDetails)}
        />
        <Button
          iconLeft={<MdOutlineFaceRetouchingOff size={22} />}
          color="gray"
          label={t("unassign_student")}
          onClick={() => openUnassignStudentModal(studentDetails)}
        />
        {/* <button
          onClick={() => openDeleteStudentModal(studentDetails)}
          className="mb-2 w-full rounded-lg bg-red-500 px-4 py-1 font-medium text-white transition duration-300 hover:scale-[1.02] hover:bg-red-600 lg:mb-0 lg:w-fit"
        >
          {t("delete_account")}
        </button> */}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        {/* Teacher Info Section */}
        <div className="col-span-12 rounded-xl bg-white p-5 shadow-md dark:bg-dark-surface lg:col-span-12 xl:col-span-6">
          <div className="flex items-center justify-between">
            <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold">{t("last_login")}:</span>
              {studentDetails?.last_login || t("not_available")}
            </div>
            <Button
              className="!px-0 hover:bg-transparent"
              variant="text"
              icon={<FaEdit size={20} />}
              onClick={() => openEditStudentModal(studentDetails)}
            />
          </div>

          <div className="flex flex-col justify-between sm:flex-col md:flex-col lg:flex-row lg:items-center">
            <div className="mb-5 flex w-full flex-grow items-center gap-6 lg:mb-0">
              <img
                className="h-28 w-28 rounded-full border-4 border-blue-500 object-cover"
                src={studentDetails?.image || user_placeholder}
                alt="icon"
              />
              <div className="max-w-full overflow-hidden">
                <h2 className="break-words text-xl font-semibold text-blue-500">{studentDetails?.name}</h2>
                <p className="break-words text-sm font-light text-mutedText dark:text-dark-mutedText">
                  {studentDetails?.username}
                </p>
              </div>
            </div>
            <div className="mx-4 hidden h-40 border-l border-[#e6e6e6] dark:border-[#FAFBFC0D] lg:block"></div>
            <div className="flex w-full flex-col items-center justify-center px-2 text-left lg:w-1/3">
              {studentScoreStats?.is_top_by_school && <img src={stars_icon} alt="icon" className="mb-1" />}
              {studentScoreStats?.is_top_student && (
                <p className="flex flex-wrap items-center text-sm font-semibold text-green-500">
                  <span className="group relative">
                    <FaTrophy className="h-12 w-12 cursor-pointer text-yellow-500" size={16} />
                    <span className="absolute left-full top-1/2 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-lg border bg-green-500 p-2 text-white shadow-2xl transition-opacity group-hover:block">
                      {t("one_of_the_top_weekly")}
                    </span>
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Classrooms and Students Summary */}
        <div
          onClick={() => navigateClassroomDetails(studentDetails?.classroom?.id)}
          className="col-span-12 flex cursor-pointer flex-col items-center justify-around rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface lg:col-span-4 xl:col-span-2"
        >
          <div className="flex flex-col items-center justify-center">
            <ClassroomIcon className="mb-4 h-14 w-14 text-mutedText dark:text-dark-mutedText" />
            <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
              {t("classroom")}
            </h3>
          </div>
          <p className="text-center text-lg font-semibold text-blue-500">{studentDetails?.classroom?.name}</p>
          <p className="text-md text-center font-semibold text-blue-500">
            {studentDetails?.classroom?.grade?.name}
          </p>
        </div>

        <div className="col-span-12 overflow-hidden rounded-xl bg-white shadow-lg dark:bg-dark-surface lg:col-span-6 xl:col-span-2">
          {/* Dark overlay */}
          <img src={studentCourses?.image} alt="Course" className="h-36 w-full object-cover" />

          <div className="flex flex-col justify-end gap-1 bg-white p-4 dark:bg-dark-surface">
            {/* Bottom content section */}
            <h4 className="w-fit bg-[#842880] px-2 text-sm uppercase text-white dark:text-gray-300 rtl:w-20">
              {t("course")}
            </h4>
            <h3 className="text-lg font-semibold text-[#842880] dark:text-white">{studentCourses?.course}</h3>
          </div>
        </div>
        <div className="col-span-12 flex flex-col items-center overflow-hidden rounded-xl bg-white p-3 shadow-lg dark:bg-dark-surface lg:col-span-12 xl:col-span-2">
          <div className="flex flex-1 flex-col items-center justify-center px-4">
            <CertificateIcon className="mb-4 h-14 w-14 text-mutedText dark:text-dark-mutedText" />
            <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
              {t("appreciation_certificate")}
            </h3>
          </div>
          <Button disabled variant="outlined" className="w-full" label={t("generate")} />
        </div>

        {/* Select Classroom, Challenges, and Playground Projects in One Row */}
        <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-12">
            {/* Classroom Report Section */}
            <div className="col-span-12 flex flex-col items-center justify-around rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-2">
              <div className="flex flex-col items-center justify-center">
                <OpenBookIcon className="mb-4 h-14 w-14 text-mutedText dark:text-dark-mutedText" />
                <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
                  {t("completed_lessons")}
                </h3>
              </div>
              <div>
                <p className="text-center text-[43px] font-semibold text-blue-500">
                  {studentLessons?.total_completed_lessons}
                </p>
                <span className="text-sm text-blue-500">
                  {t("out_of")} {studentLessons?.number_of_lessons}
                </span>
              </div>
            </div>
            <div className="col-span-12 flex flex-col items-center justify-around rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-2">
              <div className="flex flex-col items-center justify-center">
                <PulpPlaceholder className="h-15 w-15 mb-4 mt-[-12px] text-mutedText dark:text-dark-mutedText" />
                <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
                  {t("questions_rate")}
                </h3>
              </div>
              <div>
                <p className="text-center text-[38px] font-semibold text-blue-500">
                  {studentScoreStats?.question_rate}%
                </p>
              </div>
            </div>
            <div className="col-span-12 flex flex-col items-center justify-around rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-2">
              <div className="flex flex-col items-center justify-center">
                <RatedIcon className="mb-4 h-12 w-12 text-mutedText dark:text-dark-mutedText" />
                <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
                  {t("skills_rate")}
                </h3>
              </div>
              <div>
                <p className="text-center text-[38px] font-semibold text-blue-500">
                  {studentScoreStats?.skill_rate}%
                </p>
              </div>
            </div>

            {/* Total Course Challenges */}
            <div
              className={`col-span-12 flex flex-col justify-center rounded-xl bg-white p-3 shadow-md dark:bg-dark-surface lg:col-span-6 ${studentCourses?.for_children ? "xl:col-span-6" : "xl:col-span-3"}`}
            >
              <div className="px-3">
                <h3 className="font-semibold text-mutedText dark:text-dark-mutedText">
                  {studentCourses?.for_children ? t("total_course_project") : t("total_course_challenge")}
                </h3>
                <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-white">
                  {studentCourses?.for_children
                    ? (studentProjectsStats?.approved_challenges || 0) +
                      (studentProjectsStats?.pending_challenges || 0) +
                      (studentProjectsStats?.rejected_challenges || 0)
                    : (studentProjectsStats?.approved_challenges || 0) +
                      (studentProjectsStats?.pending_challenges || 0) +
                      (studentProjectsStats?.rejected_challenges || 0)}
                </p>
                <div className="my-4">
                  <p className="mb-2 flex font-medium text-blue-600">
                    <span className="flex flex-1 items-center gap-2">
                      <BluePenIcon />
                      {t("pending")}
                    </span>
                    <span className="flex flex-1 justify-end font-medium">
                      {studentProjectsStats?.pending_challenges || 0}
                    </span>
                  </p>
                  <p className="mb-2 flex font-medium text-green-500">
                    <span className="flex-2 flex items-center gap-2">
                      <GreenCheckIcon />
                      {t("approved")}
                    </span>
                    <span className="flex flex-1 justify-end font-medium">
                      {studentProjectsStats?.approved_challenges || 0}
                    </span>
                  </p>
                  <p className="mb-2 flex font-medium text-red-500">
                    <span className="flex flex-1 items-center gap-2">
                      <RedCloseIcon />
                      {t("rejected")}
                    </span>
                    <span className="flex flex-1 justify-end font-medium">
                      {studentProjectsStats?.rejected_challenges || 0}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Total Course Challenges */}
            {studentCourses?.for_children ? null : (
              <div className="col-span-12 flex flex-col justify-center rounded-xl bg-white p-3 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-3">
                <div className="px-3">
                  <h3 className="font-semibold text-mutedText dark:text-dark-mutedText">
                    {t("playground_projects")}
                  </h3>
                  <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-white">
                    {(studentProjectsStats?.approved_playground || 0) +
                      (studentProjectsStats?.pending_playground || 0) +
                      (studentProjectsStats?.rejected_playground || 0)}
                  </p>
                  <div className="my-4">
                    <p className="mb-2 flex font-medium text-blue-600">
                      <span className="flex flex-1 items-center gap-2">
                        <BluePenIcon />
                        {t("pending")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {studentProjectsStats?.pending_playground || 0}
                      </span>
                    </p>
                    <p className="mb-2 flex font-medium text-green-500">
                      <span className="flex-2 flex items-center gap-2">
                        <GreenCheckIcon />
                        {t("approved")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {studentProjectsStats?.approved_playground || 0}
                      </span>
                    </p>
                    <p className="mb-2 flex font-medium text-red-500">
                      <span className="flex flex-1 items-center gap-2">
                        <RedCloseIcon />
                        {t("rejected")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {studentProjectsStats?.rejected_playground || 0}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <AssignStudentModal ref={assignStudentModalRef} onSubmit={handleAssignStudentSuccess} />
      <UnassignStudentModal ref={unassignStudentModalRef} onSubmit={handleUnassignStudentSuccess} />
      <DeleteStudentModal ref={deleteStudentModalRef} onSubmit={handleDeleteStudentSuccess} />
      <EditStudentModal ref={editStudentModalRef} onSubmit={handleEditStudentSuccess} />
    </>
  );
};

export default SchoolStudentDetails;
